import { AxiosInstance } from 'axios';

// Configuration constants
export const DEFAULT_CACHE_TIME = 5 * 60 * 1000; // 5 minutes

/**
 * Base service class with caching and axios instance management
 */
export class BaseService {
    protected readonly axiosInstance: AxiosInstance;
    protected cache: Map<string, { data: unknown; timestamp: number }>;
    protected readonly cacheTime: number;

    constructor(axiosInstance: AxiosInstance, cacheTime: number = DEFAULT_CACHE_TIME) {
        this.axiosInstance = axiosInstance;
        this.cache = new Map();
        this.cacheTime = cacheTime;
    }

    /**
     * Gets data from cache if it exists and is not expired
     *
     * @param key - Cache key
     * @returns Cached data or undefined if not in cache or expired
     */
    protected getCachedData(key: string): any | undefined {
        const cached = this.cache.get(key);

        if (!cached) {
            return undefined;
        }

        const now = Date.now();

        // Check if cache entry is expired based on this.cacheTime
        if (now - cached.timestamp > this.cacheTime) {
            this.cache.delete(key);
            return undefined;
        }

        return cached.data;
    }

    /**
     * Sets data in cache
     *
     * @param key - Cache key
     * @param data - Data to cache
     */
    protected setCachedData(key: string, data: any): void {
        this.cache.set(key, {
            data,
            timestamp: Date.now(),
        });
    }

    /**
     * Clears the entire cache
     */
    clearCache(): void {
        this.cache.clear();
    }

    /**
     * Invalidates a specific cache key
     *
     * @param key - Cache key to invalidate
     */
    invalidateCache(key: string): void {
        this.cache.delete(key);
    }
}
